import { FunctionComponent, h } from 'preact';
import Map from '../../../components/map/map';
import { observer } from 'mobx-react';
import { useStore } from '../../../helpers/use-store';
import { useCallback, useMemo, useState } from 'preact/hooks';
import { MarkerData } from '../../../components/map/map-marker/map-marker';
import { useTheme } from '../../../hooks/use-theme/use-theme';
import Eta from '../../../components/eta/eta';
import { useTranslation } from '../../../hooks/use-translation';
import DriverArrived from '../../../components/driver-arrived/driver-arrived';
import { Location } from '@bringg/types';
import { getDriverMarker, getSelectPickupMarkers, getTaskMarker } from '../tracking-utils';
import TrackingDriverRoute from '../tracking-driver-route/tracking-driver-route';
import classNames from 'classnames';
import SelectPickupLocation from '../../../components/select-pickup-location/select-pickup-location';
import { get as _get, map as _map, isNil as _isNil } from 'lodash';
import TrackingLabels from '../tracking-labels/tracking-labels';

const TrackingMap: FunctionComponent = observer(() => {
	const {
		driverTrackingStore: {
			taskLocation,
			driverLocation,
			eta,
			wayPointArrived,
			driverTravelMode,
			customerExperience,
			trackingStatus
		}
	} = useStore();
	const {
		sharedLocationStore: { sharedLocationConfig }
	} = useStore();
	const { ariaStore } = useStore();
	const {
		employee_image,
		destination,
		driver_arrived_caption_string,
		driver_arrived_string,
		employee_name,
		privateMode,
		max_zoom,
		deliveryPin,
		hide_footer,
		show_delivery_status,
		destinationPin,
		allow_street_view,
		route,
		numberOfTasksLeftInRun,
		redirectDeliveryConfiguration,
		customer_app_labels,
		is_green_delivery
	} = customerExperience;

	const [isSelectPickupVisible, setSelectPickupVisibility] = useState(false);
	const [selectedPickup, setSelectedPickup] = useState(0);
	const { primaryColor } = useTheme();
	const { t } = useTranslation();

	const shouldShowPickupLocation = useCallback(() => {
		return _get(sharedLocationConfig, 'redirectDeliveryConfiguration.enabled') && !sharedLocationConfig.expired;
	}, [sharedLocationConfig]);

	const markers = useMemo<MarkerData[]>(() => {
		if (isSelectPickupVisible) {
			return getSelectPickupMarkers(_get(redirectDeliveryConfiguration, 'teams'), selectedPickup);
		}

		return [
			{ position: driverLocation, icon: getDriverMarker(deliveryPin, primaryColor) },
			{ position: taskLocation, icon: getTaskMarker(destinationPin) }
		];
	}, [taskLocation, driverLocation, isSelectPickupVisible, selectedPickup]);

	const center = useMemo<google.maps.LatLngLiteral>(() => {
		if (isSelectPickupVisible && markers.length) {
			return markers[selectedPickup].position;
		}
	}, [isSelectPickupVisible, markers, selectedPickup]);

	const EtaTranslations = {
		arrivalTimeTitle: t('TRACKING.ARRIVING_AT'),
		countDownTitle: t('TRACKING.ETA'),
		countDownMinuteSubtitle: t('TRACKING.MIN')
	};

	const routePath: Location[] = useMemo<Location[]>(
		() => [
			{ lat: driverLocation.lat, lng: driverLocation.lng },
			{ lat: taskLocation.lat, lng: taskLocation.lng }
		],
		[customerExperience, driverLocation, taskLocation]
	);

	const maxZoom = privateMode ? max_zoom : null;
	const shouldDisplayRouteInfo = Boolean(route && numberOfTasksLeftInRun);
	const shouldEnablePolyline = !privateMode && !shouldDisplayRouteInfo;
	const statusBarActive = show_delivery_status && !_isNil(trackingStatus);

	return (
		<div
			className={classNames('tracking-map', { 'tracking-map-without-footer': hide_footer })}
			data-testid="tracking-map"
			aria-hidden={ariaStore.isModalOpen}
		>
			{shouldDisplayRouteInfo && !statusBarActive && <TrackingDriverRoute stops={numberOfTasksLeftInRun} />}
			{wayPointArrived && (
				<DriverArrived
					driverName={employee_name}
					driverImage={employee_image}
					address={destination}
					driverArrivedCaptionString={driver_arrived_caption_string}
					driverArrivedString={driver_arrived_string}
				/>
			)}

			{eta && (
				<Eta
					time={new Date(eta)}
					translations={EtaTranslations}
					className={classNames('tracking-map-eta', { 'driver-route': shouldDisplayRouteInfo })}
					backgroundColor={primaryColor}
					showGreenDelivery={is_green_delivery}
				/>
			)}

			{customer_app_labels && <TrackingLabels trackingLabels={customer_app_labels} />}

			{shouldShowPickupLocation() && (
				<SelectPickupLocation
					onSelectedChanged={setSelectedPickup}
					onSelectVisibilityChanged={setSelectPickupVisibility}
				/>
			)}

			<Map
				center={center}
				markers={markers}
				routePath={routePath}
				travelMode={driverTravelMode}
				maxZoom={maxZoom}
				enablePolyline={shouldEnablePolyline}
				enableStreetView={allow_street_view}
				topActionsClass={shouldDisplayRouteInfo && 'driver-route'}
			/>
		</div>
	);
});

export default TrackingMap;
